import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaChevronDown } from 'react-icons/fa';

const Bar = styled.header`
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const Wrap = styled.div`
  max-width: ${({ theme }) => theme.container};
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  position: relative;
`;

const Brand = styled(Link)`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.green[700]};
  font-size: 1.25rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  padding: 4px 8px;
  gap: 8px;
  &:hover {
    color: ${({ theme }) => theme.colors.orange[700]};
  }
`;

const Logo = styled.img`
  height: 60px;
  width: auto;
  display: block;
`;

const BrandText = styled.span``;

const MenuToggle = styled.button`
  display: none;
  border: none;
  background: transparent;
  padding: 8px;
  border-radius: 999px;
  cursor: pointer;

  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    font-size: 20px;
    color: ${({ theme }) => theme.colors.gray[800]};
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 16px;

  @media (max-width: 768px) {
    position: absolute;
    top: 100%;
    right: 16px;
    margin-top: 8px;
    background: ${({ theme }) => theme.colors.white};
    box-shadow: ${({ theme }) => theme.shadows.md};
    border-radius: 12px;
    padding: 12px;
    flex-direction: column;
    min-width: 200px;
    display: ${({ $open }) => ($open ? 'flex' : 'none')};
    z-index: 10;
  }
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.gray[800]};
  padding: 8px 10px;
  border-radius: 999px;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;
  &:hover {
    background: ${({ theme }) => theme.colors.gray[100]};
  }
  ${({ $active, theme }) => $active ? `
    background: ${theme.colors.green[50]};
    color: ${theme.colors.green[800]};
  ` : ''}
`;

const DropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }
`;

const DropdownButton = styled.button`
  color: ${({ theme }) => theme.colors.gray[800]};
  padding: 8px 10px;
  border-radius: 999px;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  font-size: 16px;
  
  &:hover {
    background: ${({ theme }) => theme.colors.gray[100]};
  }
  
  svg {
    font-size: 12px;
    margin-top: 2px;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }

  ${({ $active, theme }) => $active ? `
    background: ${theme.colors.green[50]};
    color: ${theme.colors.green[800]};
  ` : ''}
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border-radius: 12px;
  padding: 8px;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  min-width: 200px;
  z-index: 20;
  margin-top: 8px;

  @media (max-width: 768px) {
    position: static;
    box-shadow: none;
    border-left: 2px solid ${({ theme }) => theme.colors.gray[200]};
    margin: 4px 0 4px 12px;
    padding-left: 8px;
    background: transparent;
    min-width: auto;
    width: calc(100% - 12px);
  }
`;

const DropdownItem = styled(Link)`
  padding: 8px 12px;
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  border-radius: 8px;
  font-size: 0.95rem;
  &:hover {
    background: ${({ theme }) => theme.colors.gray[50]};
    color: ${({ theme }) => theme.colors.green[700]};
  }

  ${({ $active, theme }) => $active ? `
    background: ${theme.colors.green[50]};
    color: ${theme.colors.green[800]};
  ` : ''}
`;

const Header = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  const isActive = (to) => pathname === to;
  
  const handleToggle = () => {
    setOpen((prev) => !prev);
  };
  
  const isActivePath = (paths) =>
    paths.some((path) => pathname === path || pathname.startsWith(`${path}/`));

  const handleNavClick = () => {
    setOpen(false);
    setActiveDropdown(null);
  };

  const toggleDropdown = (name) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveDropdown((prev) => (prev === name ? null : name));
  };

  const aboutActive = isActivePath(['/objectives', '/partnerships', '/codex-activities', '/team', '/services']);
  const guidelinesActive = isActivePath(['/fa-act']);
  const mediaActive = isActivePath(['/news', '/gallery']);
  const supportActive = isActivePath(['/careers', '/career', '/contact', '/business-registry', '/business/login']);

  return (
    <Bar>
      <Wrap>
        <Brand to="/">
          <Logo src="/assests/logo/logo.jpeg" alt="Food Authority Liberia logo" />
          <BrandText>FAL</BrandText>
        </Brand>
        <MenuToggle
          type="button"
          aria-label="Toggle navigation"
          aria-expanded={open}
          onClick={handleToggle}
        >
          <FaBars />
        </MenuToggle>
        <Nav $open={open}>
          <NavLink to="/" $active={isActive('/')} onClick={handleNavClick}>Home</NavLink>

          <DropdownContainer>
            <DropdownButton
              onClick={toggleDropdown('about')}
              type="button"
              aria-haspopup="true"
              aria-expanded={activeDropdown === 'about'}
              $active={aboutActive}
            >
              About Us <FaChevronDown />
            </DropdownButton>
            <DropdownMenu $isOpen={activeDropdown === 'about'}>
              <DropdownItem to="/team" $active={isActive('/team')} onClick={handleNavClick}>Team</DropdownItem>
              <DropdownItem to="/services" $active={isActive('/services')} onClick={handleNavClick}>Services</DropdownItem>
              <DropdownItem to="/partnerships" $active={isActive('/partnerships')} onClick={handleNavClick}>Partners</DropdownItem>
              <DropdownItem to="/codex-activities" $active={isActive('/codex-activities')} onClick={handleNavClick}>Food Safety activities and standard practices</DropdownItem>
              <DropdownItem to="/objectives" $active={isActive('/objectives')} onClick={handleNavClick}>Objectives</DropdownItem>
            </DropdownMenu>
          </DropdownContainer>

          <DropdownContainer>
            <DropdownButton
              onClick={toggleDropdown('guidelines')}
              type="button"
              aria-haspopup="true"
              aria-expanded={activeDropdown === 'guidelines'}
              $active={guidelinesActive}
            >
              Guidelines <FaChevronDown />
            </DropdownButton>
            <DropdownMenu $isOpen={activeDropdown === 'guidelines'}>
              <DropdownItem to="/fa-act" $active={isActive('/fa-act')} onClick={handleNavClick}>FAL Act</DropdownItem>
            </DropdownMenu>
          </DropdownContainer>

          <DropdownContainer>
            <DropdownButton
              onClick={toggleDropdown('media')}
              type="button"
              aria-haspopup="true"
              aria-expanded={activeDropdown === 'media'}
              $active={mediaActive}
            >
              Media <FaChevronDown />
            </DropdownButton>
            <DropdownMenu $isOpen={activeDropdown === 'media'}>
              <DropdownItem to="/news" $active={isActive('/news')} onClick={handleNavClick}>News</DropdownItem>
              <DropdownItem to="/gallery" $active={isActive('/gallery')} onClick={handleNavClick}>Gallery</DropdownItem>
            </DropdownMenu>
          </DropdownContainer>

          <DropdownContainer>
            <DropdownButton
              onClick={toggleDropdown('support')}
              type="button"
              aria-haspopup="true"
              aria-expanded={activeDropdown === 'support'}
              $active={supportActive}
            >
              Support <FaChevronDown />
            </DropdownButton>
            <DropdownMenu $isOpen={activeDropdown === 'support'}>
              <DropdownItem to="/careers" $active={isActive('/careers') || isActive('/career')} onClick={handleNavClick}>Careers</DropdownItem>
              <DropdownItem to="/contact" $active={isActive('/contact')} onClick={handleNavClick}>Contact</DropdownItem>
              <DropdownItem to="/business-registry" $active={isActive('/business-registry')} onClick={handleNavClick}>Business Registry</DropdownItem>
              <DropdownItem to="/business/login" $active={isActive('/business/login')} onClick={handleNavClick}>Food Premises Login</DropdownItem>
            </DropdownMenu>
          </DropdownContainer>
        </Nav>
      </Wrap>
    </Bar>
  );
};

export default Header;
